import type { JSX } from 'react'
import { useAuth } from '@/auth/context'

/**
 * "AugRank support is viewing your account" — the S-B11 exit criterion, seen from
 * the **customer's** side.
 *
 * Screen 08 requires an unmistakable in-app banner on an impersonated session, and
 * this is the half that is in the tenant application rather than in the staff
 * console: the borrowed session is a cookie on `app.augrank.com`, so it is *this*
 * app the staff member is looking at, and it is this app that has to say so.
 *
 * **Red, full width, above everything, and there is no way to dismiss it.** No
 * close button, no collapsed state, no `localStorage` flag. It is rendered by
 * `AppShell` outside the routed area, so no screen can omit it and it cannot be
 * scrolled away from.
 *
 * **The banner is drawn for the customer, not for the staff member**, and the
 * wording is chosen accordingly: it says what is happening, that it ends by itself,
 * and where to see the record. It does not say *why* — the reason is in the audit
 * log and in the email they were sent, which are the two places it belongs. A
 * reason quoted in a banner over their own screen would be AugRank explaining
 * itself in the middle of somebody's work.
 *
 * **It said "in your audit log under Settings" and there is no such screen.** Found
 * on the first live support session, in a browser, by the owner looking for the
 * entry the banner had just promised. The entries are real — `impersonation.start`
 * and `impersonation.end` are written into the tenant's own hash-chained log inside
 * the same transaction as the grant, and `GET /api/v1/audit` serves them — but
 * `web/` has no panel that reads it, so Settings offers organisation, team, budget,
 * notifications, sessions and danger-zone, and nothing else.
 *
 * The copy now names the record without naming a route, because the alternative was
 * to invent a screen: there is no approved design for a tenant-side audit view
 * (`design/screens/` has none, and CLAUDE.md rule 5 puts screens behind the design
 * gate), and a banner that sends somebody somewhere empty is worse than one that
 * does not send them anywhere. **The screen is owed** and is recorded as such in
 * `PROGRESS.md` — the API it needs has existed since S-B12.
 *
 * `null` on every ordinary session, which is one attribute read.
 */
export function SupportAccessBanner(): JSX.Element | null {
  const { session } = useAuth()
  const mode = session?.impersonation_mode
  if (!mode) return null

  return (
    <div className="app-support-banner" role="alert">
      <span aria-hidden="true">⚠</span>
      <span>
        <strong>AugRank support is viewing your account</strong> —{' '}
        {mode === 'write' ? (
          <>
            with permission to make changes, approved by a second member of their staff. This ends
            automatically within fifteen minutes.
          </>
        ) : (
          <>
            read-only. Nothing can be approved, published or changed. This ends automatically within
            the hour.
          </>
        )}{' '}
        You were emailed when it started, and both the start and the end are recorded in your
        organisation&rsquo;s audit log.
      </span>
    </div>
  )
}
